import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { cn } from "@/ui/lib/utils";
import { ClassificationReviewItem } from "../../types/review";
import { Checkbox } from "@/ui/components/ui/checkbox";
import { useLocalReviewStore } from "../../stores/useLocalReviewStore";
import { useGetClassificationTaxonomy } from "../../api/queries/taxonomy.queries";
import { Check, AlertTriangle, Clock, Bot, UserCheck, Eye, ImageOff } from "lucide-react";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";
import { useSignedImage } from "../../hooks/useSignedImage";
import { useReviewLayoutStore } from "../../stores/useReviewLayoutStore";
import { getCardEmphasisColor, isEmphasisLabel } from "../../utils/label-emphasis";
import { alertStatusStyle } from "@/alerts/utils/alert-status-style";
import { alertStatusLabel, alertTypeLabel } from "@/alerts/types/machine-alert";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { eventStateLabel, getEventPresentation } from "../../utils/event-colors";
import LabelTierBadge from "./label-tier-badge";

/* ──────────────────────────────────────────────────────────────────
 * ReviewCard — grid tile for the Classification Review page.
 *
 * The card's job is TRIAGE, not analysis. It answers one question:
 * "Does this image need my attention?"
 *
 * Design decisions:
 *  - Primary label shown as the main chip on the image overlay.
 *  - Other labels collapsed into a "+N" counter — details in the
 *    detail panel.
 *  - Confidence: single "min across all labels" — weakest-link
 *    principle. If ONE label is uncertain the reviewer should look,
 *    even if the rest are 0.95.
 *  - Legacy labels: amber dashed border + "legacy" marker.
 *  - Review state: small coloured dot, not a badge.
 * ────────────────────────────────────────────────────────────────── */

interface ReviewCardProps {
  item: ClassificationReviewItem;
  isSelected: boolean;
  onToggleSelect: (imageId: number) => void;
  onClick: (item: ClassificationReviewItem) => void;
  /**
   * Colour for the dashed "same event" group border, or null when this card
   * isn't part of a multi-image event run. See `utils/event-grouping.ts`.
   */
  groupColor?: string | null;
}

/* ──────────────────────────────────────────────────────────────────
 * ReviewDot — small coloured indicator for review state
 * ────────────────────────────────────────────────────────────────── */

const ReviewDot = ({
  state,
  t,
}: {
  state: string;
  t: TFunction<"mlExports">;
}) => {
  /** `review_state` is a wire value; only the tooltip text is translated. */
  const config: Record<string, { color: string; title: string }> = {
    MODEL_ONLY: { color: "bg-zinc-400", title: t("card.reviewDot.modelOnly") },
    MODEL_CORRECTED: {
      color: "bg-emerald-400",
      title: t("card.reviewDot.modelCorrected"),
    },
    REVIEW_OUTDATED: {
      color: "bg-amber-400",
      title: t("card.reviewDot.reviewOutdated"),
    },
    HUMAN_ADDED: {
      color: "bg-violet-400",
      title: t("card.reviewDot.humanAdded"),
    },
  };
  const c = config[state] ?? { color: "bg-zinc-400", title: state };

  return (
    <span
      className={cn(
        "block h-2.5 w-2.5 rounded-full ring-2 ring-background shrink-0",
        c.color,
      )}
      title={c.title}
    />
  );
};

const ReviewCard = ({
  item,
  isSelected,
  onToggleSelect,
  onClick,
  groupColor = null,
}: ReviewCardProps) => {
  const { t } = useTranslation("mlExports");
  const localOverride = useLocalReviewStore((s) => s.getOverride(item.image_id));
  const { data: taxonomy } = useGetClassificationTaxonomy();
  const emphasisLabel = useReviewLayoutStore((s) => s.emphasisLabel);
  const emphasisEnabled = useReviewLayoutStore((s) => s.emphasisEnabled);
  const emphasisColor = getCardEmphasisColor(
    item,
    emphasisLabel,
    emphasisEnabled,
  );
  const { src: imageSrc, onError: handleImageError, hasFailed } = useSignedImage({
    imageId: item.image_id,
    initialUrl: item.signed_url,
  });

  const legacySet = useMemo(() => {
    if (!taxonomy?.other_labels) return new Set<string>();
    return new Set(
      taxonomy.other_labels
        .filter((l) => l.category === "legacy")
        .map((l) => l.canonical),
    );
  }, [taxonomy]);

  // No special treatment for is_primary — it's a backend internal.
  // Just take the first label for the card headline.
  const firstLabel = item.labels?.[0] ?? null;
  const classification =
    localOverride?.humanLabel ??
    firstLabel?.final_label ??
    firstLabel?.classification ??
    "—";
  const reviewState = firstLabel?.review_state;

  const hasLegacy = useMemo(() => {
    if (!item.labels || legacySet.size === 0) return false;
    return item.labels.some((l) => legacySet.has(l.final_label));
  }, [item.labels, legacySet]);

  // ─── Aggregate confidence: min across ALL active labels ──────────
  // Weakest-link: if any label is uncertain, the card signals
  // "needs attention" regardless of how confident the rest are.
  const { minConfidence, labelCount } = useMemo(() => {
    if (!item.labels || item.labels.length === 0)
      return { minConfidence: null, labelCount: 0 };

    let min: number | null = null;
    for (const l of item.labels) {
      if (l.confidence != null) {
        min = min === null ? l.confidence : Math.min(min, l.confidence);
      }
    }
    return { minConfidence: min, labelCount: item.labels.length };
  }, [item.labels]);

  // Parent-event state badge — read straight off the listing item, so the
  // grid never resolves an event per card.
  const eventPresentation = getEventPresentation(item.event_state);

  // ─── Most recent reviewer ────────────────────────────────────────
  const reviewMeta = useMemo(() => {
    if (!item.labels) return null;
    let latest: { name: string; at: string } | null = null;
    for (const l of item.labels) {
      if (l.review_state === "MODEL_ONLY" || !l.reviewed_at) continue;
      const name =
        l.reviewer?.full_name || l.reviewer?.username || l.reviewed_by || null;
      if (!name) continue;
      if (!latest || l.reviewed_at > latest.at) {
        latest = { name, at: l.reviewed_at };
      }
    }
    return latest;
  }, [item.labels]);


  return (
    <div
      data-review-card
      data-image-id={item.image_id}
      className={cn(
        "relative group rounded-lg overflow-hidden cursor-pointer transition-all duration-200",
        "bg-card",
        // Same-event cards share a dashed border in an event-derived colour;
        // isolated cards keep the default subtle solid border.
        groupColor ? "border-2 border-dashed" : "border border-border/60",
        "hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20 hover:-translate-y-0.5",
        isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        localOverride && "ring-2 ring-green-500/60 ring-offset-2 ring-offset-background",
      )}
      style={groupColor ? { borderColor: groupColor } : undefined}
      title={
        groupColor
          ? t("card.eventTitle", { uuid: item.event_uuid.slice(0, 8) })
          : undefined
      }
      onClick={() => onClick(item)}
    >
      {/* ─── Checkbox ────────────────────────────────────────── */}
      <div
        className="absolute top-2 left-2 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(item.image_id);
        }}
      >
        <Checkbox
          checked={isSelected}
          className="bg-background/80 backdrop-blur-sm border-border/80"
        />
      </div>

      {/* ─── Top-right badge stack ───────────────────────────────
          The unsaved-changes marker sits above the alert verdict: it is the
          transient one, and it must not be pushed out of view by a badge that
          is the same for every frame of the event. The parent-event state
          comes last — it is the most static of the three. */}
      {(localOverride || item.alert || item.event_state) && (
        <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
          {localOverride && (
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-500/90 text-white text-[9px] font-bold uppercase tracking-wider"
              title={t("card.unsavedChanges")}
            >
              <Check className="h-2.5 w-2.5" />
              {t("card.corrected")}
            </div>
          )}
          {item.alert && (
            <div
              className={cn(
                "px-1.5 py-0.5 rounded-md border backdrop-blur-sm text-[9px] font-bold uppercase tracking-wider",
                alertStatusStyle(item.alert.status),
              )}
              title={t(
                item.alert.closed_by ? "card.alertTitleBy" : "card.alertTitle",
                {
                  type: alertTypeLabel(item.alert.type),
                  status: alertStatusLabel(item.alert.status),
                  closedBy: item.alert.closed_by,
                },
              )}
            >
              {alertStatusLabel(item.alert.status)}
            </div>
          )}
          {item.event_state && (
            <div
              className={cn(
                "px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider",
                eventPresentation.classes,
              )}
              title={t("card.parentEvent", {
                state: eventStateLabel(item.event_state, t).toLowerCase(),
              })}
            >
              {eventStateLabel(item.event_state, t)}
            </div>
          )}
        </div>
      )}

      {/* ─── Image ───────────────────────────────────────────── */}
      <div className="aspect-square bg-muted relative overflow-hidden">
        {hasFailed ? (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <ImageOff className="h-8 w-8 text-muted-foreground" />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={t("card.imageAlt", { id: item.image_id })}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
            onError={handleImageError}
          />
        )}

        {/* Gradient overlay */}
        {labelCount > 0 && (
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
        )}

        {/* ─── Label chips — all labels, no confidence ─────────── */}
        {item.labels && item.labels.length > 0 && (
          <div className="absolute bottom-0 inset-x-0 p-1.5 flex flex-wrap gap-1 items-end">
            {item.labels.slice(0, 4).map((label) => {
              const legacy = legacySet.has(label.final_label);
              const emphasised = isEmphasisLabel(
                label,
                emphasisLabel,
                emphasisEnabled,
              );
              return (
                <div
                  key={label.id}
                  className={cn(
                    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold leading-tight backdrop-blur-md max-w-full",
                    emphasised
                      ? "text-black"
                      : legacy
                        ? "bg-amber-900/60 text-amber-200 border border-dashed border-amber-400/40"
                        : "bg-black/50 text-white/90",
                  )}
                  style={
                    emphasised && emphasisColor
                      ? {
                          backgroundColor: emphasisColor,
                          boxShadow: `0 0 8px ${emphasisColor}`,
                        }
                      : undefined
                  }
                >
                  {label.origin === "human" ? (
                    <UserCheck className="h-2.5 w-2.5 shrink-0 opacity-70" />
                  ) : (
                    <Bot className="h-2.5 w-2.5 shrink-0 opacity-50" />
                  )}
                  <span className="truncate">{label.final_label}</span>
                  <LabelTierBadge label={label} alert={item.alert} />
                </div>
              );
            })}
            {item.labels.length > 4 && (
              <span className="text-[9px] font-mono text-white/50 px-1 py-0.5 rounded bg-black/30 backdrop-blur-sm">
                +{item.labels.length - 4}
              </span>
            )}
          </div>
        )}

        {/* ─── Min confidence bar ────────────────────────────── */}
        {minConfidence != null && (
          <div className="absolute bottom-0 inset-x-0 h-[2px] bg-black/20">
            <div
              className={cn(
                "h-full transition-all",
                minConfidence >= 0.8
                  ? "bg-emerald-400"
                  : minConfidence >= 0.5
                    ? "bg-amber-400"
                    : "bg-red-400",
              )}
              style={{ width: `${Math.round(minConfidence * 100)}%` }}
            />
          </div>
        )}

        {/* ─── Invalid frame overlay ─────────────────────────── */}
        {!item.dump_image_valid && (
          <div className="absolute inset-0 bg-yellow-900/20 flex items-center justify-center">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-yellow-600/90 text-white text-[10px] font-bold uppercase tracking-wider">
              <AlertTriangle className="h-3 w-3" />
              {t("card.invalid")}
            </div>
          </div>
        )}
      </div>

      {/* ─── Info bar ────────────────────────────────────────── */}
      <div className="px-2.5 py-2 space-y-0.5">
        <div className="flex items-center justify-between gap-1">
          <span className="inline-flex items-center gap-1.5 min-w-0">
            {reviewState && <ReviewDot state={reviewState} t={t} />}
            <span
              className={cn(
                "text-xs font-semibold truncate",
                localOverride && "text-green-600 dark:text-green-400",
              )}
            >
              {classification}
            </span>
          </span>
          {minConfidence != null && (
            <span
              className={cn(
                "text-[10px] font-mono shrink-0",
                minConfidence >= 0.8
                  ? "text-emerald-600 dark:text-emerald-400"
                  : minConfidence >= 0.5
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-red-500",
              )}
              title={t("card.minConfidence")}
            >
              {t("card.percent", {
                value: formatNumber(Math.round(minConfidence * 100)),
              })}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="truncate">
            {t("card.machineFrame", {
              machine: item.machine_name,
              frame: item.event_frame,
            })}
          </span>
          {hasLegacy && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-dashed border-amber-500/50 text-amber-600 dark:text-amber-400 font-medium shrink-0">
              <Clock className="h-2.5 w-2.5" />
              {t("card.legacy")}
            </span>
          )}
        </div>

        {/* ─── Review metadata ──────────────────────────────── */}
        {reviewMeta && (
          <div className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
            <Eye className="h-2.5 w-2.5 shrink-0 opacity-70" />
            <span className="truncate">
              {reviewMeta.name} · {formatRelativeTimeUTC(reviewMeta.at)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;
